import { OrbitControls } from '@react-three/drei';
import type {} from '@react-three/fiber';
import type { ShieldMap } from '@chessmate/game-engine';
import type { HeatmapMode } from '@chessmate/protocol';
import { useMemo } from 'react';
import { Board } from './Board.js';
import { CoordinateLabels } from './CoordinateLabels.js';
import { HeatMap } from './HeatMap.js';
import { ProjectionReporter } from './BoardProjection.js';
import { Highlights, computeHighlights } from './Highlights.js';
import { CHESS_PLANE } from './projection.js';
import { OppArrow } from './OppArrow.js';
import { PieceMesh } from './PieceMesh.js';
import { PlyArrows } from './PlyArrows.js';
import { PromotionBand } from './PromotionBand.js';
import { Table } from './Table.js';
import type { PieceMaterialSpec } from './materials.js';
import { fenToPieces, squareToVec3 } from './scene.js';
import type { PieceSizeSpec } from './sizes.js';
import type { BoardTheme, PieceTheme, TableTheme } from './themes.js';

export interface ChessBoardProps {
  readonly fen: string;
  readonly selected: string | null;
  readonly targets: ReadonlyArray<{ square: string; capture: boolean }>;
  readonly lastMove: { from: string; to: string } | null;
  readonly checkSquare: string | null;
  /** Squares of movable pieces to ring (assistance option); null/empty = off. */
  readonly movable?: ReadonlyArray<string> | null;
  /** Squares between the selected piece and its destinations (assistance option). */
  readonly paths?: ReadonlyArray<string> | null;
  readonly heatmap: HeatmapMode;
  readonly pieceTheme?: PieceTheme;
  readonly boardTheme?: BoardTheme;
  readonly tableTheme?: TableTheme;
  readonly pieceMaterial?: PieceMaterialSpec;
  readonly pieceSize?: PieceSizeSpec;
  readonly pieceWhiteColor?: string;
  readonly pieceBlackColor?: string;
  /** Render the a-h / 1-8 file-rank labels at the board perimeter. */
  readonly showCoords?: boolean;
  /** Look-ahead: the engine's PV chain after the player's intended move.
   *  Index 0 = opponent's reply (cyan rings), 1 = player's counter-reply
   *  (mint rings), 2 = opponent's next reply (cyan), etc. Capped by the
   *  theme-store depth setting (max 3). */
  readonly predictedReply?: ReadonlyArray<{ from: string; to: string }> | null;
  /** Animated arrow tying together the opponent's last-move origin and destination. */
  readonly opponentArrow?: { from: string; to: string } | null;
  /** Defence/attack summary for the viewer's own pieces, from
   *  `buildShieldMap`. Already scoped to one colour by the builder. */
  readonly shields?: ShieldMap | null;
  /** Square whose shield / threat detail is expanded. */
  readonly reveal?: string | null;
  /** Map used to resolve `reveal`. Defaults to `shields`; the screen passes
   *  the *opponent's* map when the revealed square holds an enemy piece,
   *  since the viewer's map never records enemy pieces as subjects. */
  readonly revealShields?: ShieldMap | null;
  /** Checkers only: the colour whose crown row gets the promotion band.
   *  Null (and chess games) render nothing. The screen gates on
   *  `adapter.gameId === 'checkers'` — the band has no chess meaning. */
  readonly promotionRow?: 'w' | 'b' | null;
  /** Receives the CSS `matrix3d` that registers the DOM tap overlay with this
   *  board as the camera sees it (see `projection.ts`). Omit when there is no overlay. */
  readonly onOverlayTransform?: (matrix3d: string) => void;
}

export function ChessBoard(props: ChessBoardProps) {
  const {
    fen,
    selected,
    targets,
    lastMove,
    checkSquare,
    movable = null,
    paths = null,
    heatmap,
    pieceTheme,
    boardTheme,
    tableTheme,
    pieceMaterial,
    pieceSize,
    pieceWhiteColor,
    pieceBlackColor,
    showCoords = false,
    predictedReply = null,
    opponentArrow = null,
    shields = null,
    reveal = null,
    revealShields = null,
    promotionRow = null,
    onOverlayTransform,
  } = props;
  const pieces = useMemo(() => fenToPieces(fen), [fen]);
  const highlights = useMemo(
    () =>
      computeHighlights({
        selected,
        movable,
        paths,
        targets,
        lastMove,
        checkSquare,
        predictedReply,
        shields,
        reveal,
        revealShields,
      }),
    [selected, movable, paths, targets, lastMove, checkSquare, predictedReply, shields, reveal, revealShields],
  );

  return (
    <>
      {/* Lifted from `#14121c` to `#1d1b26` so dark pieces don't disappear into
          the frame; the differential against `classic.black` (`#3d3a48`) is
          preserved. */}
      <color attach="background" args={['#1d1b26']} />
      {/* Four-light setup — ambient (down so the directionals show),
          key from top-right, warm fill from the left at ~45°, cool rim from
          below-back to separate pieces from the board. Shadow casting is off:
          three.js shadow maps are expensive and unnecessary here, the lights
          are purely cosmetic. */}
      <ambientLight intensity={0.55} />
      <directionalLight position={[6, 7, 5]} intensity={1.0} color="#ffffff" />
      <directionalLight position={[-5, 3, 4]} intensity={0.55} color="#ffe6c2" />
      <directionalLight position={[0, -2, -6]} intensity={0.25} color="#a0b4ff" />
      {/* Scene camera (inert in R3F v9 — the app sets the active one via <Canvas camera>). */}
      <perspectiveCamera position={[0, 9, -9]} fov={45} />
      {/* Rotation is locked to match the fixed-orientation DOM tap overlay (SquareOverlay); dragging would desync the square under the kid's finger from the one reported on tap. */}
      <OrbitControls
        enablePan={false}
        enableRotate={false}
        minDistance={6}
        maxDistance={16}
        maxPolarAngle={Math.PI / 2.3}
      />
      {onOverlayTransform && <ProjectionReporter plane={CHESS_PLANE} onChange={onOverlayTransform} />}
      <Table theme={tableTheme} />
      <Board theme={boardTheme} />
      {/* Promotion band sits at the bottom of the overlay stack (y=0.012),
          below wash/rings/heat — see PromotionBand's module comment. Null for
          chess, so the mesh never even mounts. */}
      {promotionRow && <PromotionBand color={promotionRow} />}
      <Highlights
        highlights={highlights}
        targets={targets}
        lastMove={lastMove}
        predictedReply={predictedReply}
      />
      {/* Multi-ply look-ahead arrows — one per ply in the chain, rendered
          after Highlights so the arrowhead sits above the rings. Empty
          chain or `null` predictedReply → no meshes (zero cost). */}
      {predictedReply && predictedReply.length > 0 && <PlyArrows chain={predictedReply} />}
      {/* Opponent arrow sits above the rings (y=0.06 vs 0.04) so the
          coral-on-amber stack reads cleanly. OppArrow no-ops when prop is
          null, so the player's own last move pays no cost. */}
      {opponentArrow && <OppArrow from={opponentArrow.from} to={opponentArrow.to} />}
      {showCoords && <CoordinateLabels />}
      <HeatMap fen={fen} mode={heatmap} />
      {pieces.map((p) => (
        <PieceMesh
          key={`${p.square}-${p.char}`}
          char={p.char}
          position={squareToVec3(p.square)}
          theme={pieceTheme}
          material={pieceMaterial}
          size={pieceSize}
          whiteColor={pieceWhiteColor}
          blackColor={pieceBlackColor}
        />
      ))}
    </>
  );
}
